import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../config/axiosConfig';
import usePermissionRefresh from '../../hooks/usePermissionRefresh';

const ERROR_MAP = {
  'Usuario no encontrado': {
    title: 'Correo no registrado',
    message: 'No encontramos una cuenta asociada a este correo electrónico.',
    guide: 'Verifica que la dirección sea correcta. Si eres nuevo usuario, solicita acceso al administrador del sistema.',
    field: 'email',
  },
  'Usuario inactivo': {
    title: 'Cuenta inactiva',
    message: 'Tu cuenta está desactivada en el sistema.',
    guide: 'Contacta al administrador para solicitar la reactivación de tu acceso.',
    field: null,
  },
  'Usuario deshabilitado': {
    title: 'Cuenta deshabilitada',
    message: 'Tu cuenta no tiene acceso al sistema en este momento.',
    guide: 'Contacta al administrador para verificar el estado de tu cuenta.',
    field: null,
  },
  'Contraseña incorrecta': {
    title: 'Contraseña incorrecta',
    message: 'La contraseña ingresada no coincide con la registrada.',
    guide: 'Verifica que no tengas el Bloq Mayús activado e intenta de nuevo.',
    field: 'password',
  },
};

const getErrorInfo = (err) => {
  const body = err.response?.data;
  const status = err.response?.status;
  if (ERROR_MAP[body]) return ERROR_MAP[body];
  if (status === 500) return {
    title: 'Servicio no disponible',
    message: 'Ocurrió un error en el servidor.',
    guide: 'Por favor intenta más tarde o contacta al administrador.',
    field: null,
  };
  return {
    title: 'Error de acceso',
    message: 'No fue posible iniciar sesión.',
    guide: 'Verifica tus credenciales e intenta de nuevo.',
    field: null,
  };
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshPermissionsAfterLogin } = usePermissionRefresh();

  // Diálogo de "Olvidé mi contraseña"
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('error') === 'permisos_no_configurados') {
      setErrorInfo({
        title: 'Permisos no configurados',
        message: 'Su rol no tiene permisos asignados en el sistema.',
        guide: 'Contacte al administrador para configurar los permisos necesarios.',
        field: null,
      });
    } else if (urlParams.get('error') === 'sesion_expirada') {
      setErrorInfo({
        title: 'Sesión expirada',
        message: 'Tu sesión terminó o el token ya no es válido, por lo que se cerró automáticamente.',
        guide: 'Inicia sesión de nuevo. Si estabas subiendo un archivo o guardando cambios, deberás repetir esa acción.',
        field: null,
      });
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorInfo(null);

    if (!email || !password) {
      setErrorInfo({ title: 'Campos requeridos', message: 'Por favor completa todos los campos.', guide: null, field: null });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/users/login', { email, password });

      if (response.data && response.data.token) {
        sessionStorage.setItem('authToken', response.data.token);
        if (onLogin) onLogin();
        try {
          await refreshPermissionsAfterLogin();
        } catch (permissionError) {
          // No fallar el login por error de permisos
        }
        navigate('/home');
      } else {
        setErrorInfo(getErrorInfo({}));
      }
    } catch (err) {
      setErrorInfo(getErrorInfo(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordEmail(email);
    setForgotPasswordMessage('');
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    if (forgotPasswordLoading) return;
    setForgotPasswordOpen(false);
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotPasswordEmail) return;
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');
    try {
      const response = await api.post('/users/forgot-password', { email: forgotPasswordEmail });
      setForgotPasswordMessage(response.data?.message
        || 'Si el correo existe en nuestro sistema, te enviaremos instrucciones para recuperar tu contraseña.');
    } catch (err) {
      // El backend responde el mismo mensaje genérico sin importar el resultado;
      // si de plano no hay respuesta (red caída, etc.), avisamos sin detalles.
      setForgotPasswordMessage('No se pudo procesar la solicitud. Intenta de nuevo en unos minutos.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper elevation={3} style={{ padding: '2rem', width: '100%', borderRadius: '24px'  }}>
           {/* Logo de Conem en la parte superior izquierda */}
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <img src="/logo.ico" alt="Conem Logo" style={{ height: 75 }} />
    </Box>

         {/* Línea gris delgada a la altura del 10% de la forma */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: 0,
              width: '100%',
              height: '2px',
              backgroundColor: '#ccc',
              zIndex: 2
            }}
          />


        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Inicio de sesión
          </Typography>
          <hr />

          <TextField
            fullWidth
            margin="normal"
            label="Correo electrónico"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrorInfo(null); }}
            required
            error={errorInfo?.field === 'email'}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrorInfo(null); }}
            required
            error={errorInfo?.field === 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {errorInfo && (
            <Alert severity="error" style={{ marginTop: '1rem' }}>
              <AlertTitle>{errorInfo.title}</AlertTitle>
              {errorInfo.message}
              {errorInfo.guide && (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'inherit', opacity: 0.9 }}>
                  {errorInfo.guide}
                </Typography>
              )}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            style={{ marginTop: '1rem' }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Ingresando...
              </>
            ) : (
              'Entrar'
            )}
          </Button>

          <Box style={{ marginTop: '1rem', textAlign: 'right' }}>
            <Button
              variant="text"
              onClick={handleOpenForgotPassword}
              sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
            >
              Olvidé mi contraseña
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="xs" fullWidth>
        <DialogTitle>Recuperar contraseña</DialogTitle>
        <DialogContent>
          {forgotPasswordMessage ? (
            <DialogContentText sx={{ mt: 1 }}>{forgotPasswordMessage}</DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
              </DialogContentText>
              <TextField
                fullWidth
                autoFocus
                label="Correo electrónico"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                disabled={forgotPasswordLoading}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {forgotPasswordMessage ? (
            <Button onClick={handleCloseForgotPassword} variant="contained">Cerrar</Button>
          ) : (
            <>
              <Button onClick={handleCloseForgotPassword} disabled={forgotPasswordLoading}>Cancelar</Button>
              <Button
                onClick={handleForgotPasswordSubmit}
                variant="contained"
                disabled={forgotPasswordLoading || !forgotPasswordEmail}
              >
                {forgotPasswordLoading ? 'Enviando...' : 'Enviar enlace'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>


    <Box
  sx={{
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100vw',
    height: '10vh',
    backgroundColor: '#111',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 1
  }}
>
  <span
    style={{
      color: '#fff',
      fontSize: '0.95rem',
      fontWeight: 400,
      letterSpacing: 1,
      marginRight: '0.5rem'
    }}
  >
    Powered By:
  </span>
  <a
    href="https://github.com/omardiaz"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      color: '#fff',
      textDecoration: 'underline',
      fontSize: '0.95rem',
      fontWeight: 400,
      letterSpacing: 1
    }}
  >
    Israel &amp; Omar Díaz
  </a>
</Box>

    </Container>
  );
};

export default Login;